import React, { useEffect, useMemo, useState } from 'react';
import { ArchiveIcon, ArrowUpRightIcon, BoxesIcon, CheckIcon, ChevronDownIcon, ChevronLeftIcon, ChevronRightIcon, ChevronUpIcon, ClipboardListIcon, SlidersHorizontalIcon, XIcon } from 'lucide-react';
import { CarliStars } from './CarliStars';
import { scoreConfidence } from '../utils/recoveryConfidence';
import { reconcileSubmission } from '../utils/submissionOutcome';
import { RecoveryRules, defaultRulesForBrand, rulesMatchDefaults, summarizeRules } from '../data/recoveryRules';
import { Batch, eddyIncomingBatches, onePeopleIncomingBatches, submissionId } from '../data/inventorySubmissions';
import { useNisoloProducts } from '../hooks/useNisoloProducts';
import { useNisoloSubmissions } from '../hooks/useNisoloSubmissions';
import { useNisoloOrders } from '../hooks/useNisoloOrders';
import { UploadAIInsights } from './UploadAIInsights';
import { UnitsDashboard } from './UnitsDashboard';
import { SubmissionRules } from './SubmissionRules';
type SummaryMetric = {
  label: string;
  value: string;
  detail: string;
};
const onePeopleSummaryMetrics: SummaryMetric[] = [{
  label: 'Uploaded products',
  value: '189',
  detail: 'Products'
}, {
  label: 'Available for sale products',
  value: '83',
  detail: 'Products'
}, {
  label: 'Live products',
  value: '80',
  detail: 'Products'
}, {
  label: 'Units uploaded',
  value: '1,902',
  detail: 'Units'
}, {
  label: 'Available for sale units',
  value: '1,024',
  detail: 'Units'
}, {
  label: 'Live units',
  value: '1,019',
  detail: 'Units'
}];
const emptySummaryMetrics: SummaryMetric[] = [{
  label: 'Uploaded products',
  value: '0',
  detail: 'Products'
}, {
  label: 'Available for sale products',
  value: '0',
  detail: 'Products'
}, {
  label: 'Live products',
  value: '0',
  detail: 'Products'
}, {
  label: 'Units uploaded',
  value: '0',
  detail: 'Units'
}, {
  label: 'Available for sale units',
  value: '0',
  detail: 'Units'
}, {
  label: 'Live units',
  value: '0',
  detail: 'Units'
}];
const eddySummaryMetrics: SummaryMetric[] = [{
  label: 'Uploaded products',
  value: '320',
  detail: 'Products'
}, {
  label: 'Available for sale products',
  value: '86',
  detail: 'Products'
}, {
  label: 'Live products',
  value: '82',
  detail: 'Products'
}, {
  label: 'Units uploaded',
  value: '2,981',
  detail: 'Units'
}, {
  label: 'Available for sale units',
  value: '682',
  detail: 'Units'
}, {
  label: 'Live units',
  value: '669',
  detail: 'Units'
}];
type BrandBase = {
  receivedProducts: number;
  receivedUnits: number;
  totalMsrp: number;
  maxOpportunity: number;
  soldUnits: number;
  totalSales: number;
};
type BatchRow = Batch & {
  brand: string;
  id: string;
};
type SortKey = 'uploaded' | 'batch' | 'brand' | 'units' | 'msrp' | 'recovery';
const ROWS_PER_PAGE = 50;

// Submission dates arrive as display strings ("Feb 5, 2026") and can be missing.
function uploadedTime(value: string) {
  const parsed = Date.parse(value);
  return Number.isNaN(parsed) ? Number.NEGATIVE_INFINITY : parsed;
}
const onePeopleBase: BrandBase = {
  receivedProducts: 189,
  receivedUnits: 1902,
  totalMsrp: 130993,
  maxOpportunity: 65437,
  soldUnits: 2920,
  totalSales: 49411
};
const eddyBase: BrandBase = {
  receivedProducts: 320,
  receivedUnits: 2981,
  totalMsrp: 321656,
  maxOpportunity: 160828,
  soldUnits: 4010,
  totalSales: 325875
};
const currency = (value: number) => value.toLocaleString('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0
});
export function InventoryDashboard({
  selectedOwner,
  isAdmin = false,
  onOpenSettings
}: {
  selectedOwner: string;
  isAdmin?: boolean;
  onOpenSettings?: (submissionId: string) => void;
}) {
  const allBrands = selectedOwner === '';
  const includes = (brand: string) => allBrands || selectedOwner === brand;
  const nisoloActive = includes('Nisolo');
  const {
    inventory: nisolo
  } = useNisoloProducts(nisoloActive);
  const {
    data: nisoloSubmissions,
    loading: submissionsLoading,
    error: submissionsError
  } = useNisoloSubmissions(nisoloActive);
  const {
    data: nisoloOrders
  } = useNisoloOrders(nisoloActive);
  const [section, setSection] = useState<'Submissions' | 'Units'>('Submissions');
  const [deselected, setDeselected] = useState<string[]>([]);
  const [rulesById, setRulesById] = useState<Record<string, RecoveryRules>>({});
  const rulesFor = (id: string, brand?: string, batchName?: string) => rulesById[id] ?? defaultRulesForBrand(brand, batchName);
  const setRulesFor = (id: string, rules: RecoveryRules) => setRulesById(current => ({
    ...current,
    [id]: rules
  }));
  const resetRulesFor = (id: string) => setRulesById(current => {
    const {
      [id]: _removed,
      ...rest
    } = current;
    return rest;
  });
  // Guardrails open in a dialog so the user never leaves the inventory table.
  const [guardrailRow, setGuardrailRow] = useState<BatchRow | null>(null);
  // Clicking a forecast opens that upload's Carli report.
  const [reportRow, setReportRow] = useState<BatchRow | null>(null);
  const [sort, setSort] = useState<{
    key: SortKey;
    direction: 'asc' | 'desc';
  }>({
    key: 'uploaded',
    direction: 'desc'
  });
  const [page, setPage] = useState(1);
  useEffect(() => setDeselected([]), [selectedOwner]);
  useEffect(() => setPage(1), [selectedOwner, sort]);
  const toggleSort = (key: SortKey) => setSort(current => current.key === key ? {
    key,
    direction: current.direction === 'asc' ? 'desc' : 'asc'
  } : {
    key,
    direction: key === 'batch' || key === 'brand' ? 'asc' : 'desc'
  });
  const brands = useMemo(() => {
    const entries: {
      brand: string;
      batches: Batch[];
      base: BrandBase;
    }[] = [];
    if (includes('1 People')) entries.push({
      brand: '1 People',
      batches: onePeopleIncomingBatches,
      base: onePeopleBase
    });
    if (includes('Eddy')) entries.push({
      brand: 'Eddy',
      batches: eddyIncomingBatches,
      base: eddyBase
    });
    if (nisoloActive) {
      entries.push({
        brand: 'Nisolo',
        batches: nisoloSubmissions?.submissions ?? [],
        base: {
          receivedProducts: nisolo?.totalProducts ?? 0,
          receivedUnits: nisoloSubmissions?.totalReceived ?? 0,
          totalMsrp: nisoloSubmissions?.totalMsrp ?? 0,
          maxOpportunity: nisoloSubmissions?.totalEstimatedRecovery ?? 0,
          soldUnits: nisoloOrders?.units ?? 0,
          totalSales: nisoloOrders?.sales ?? 0
        }
      });
    }
    return entries;
  }, [selectedOwner, nisolo, nisoloSubmissions, nisoloOrders]);
  const unsortedRows: BatchRow[] = useMemo(
  // Batch names repeat within a brand, so the index keeps each row's id unique.
  () => brands.flatMap(({
    brand,
    batches
  }) => batches.map((batch, index) => ({
    ...batch,
    brand,
    id: submissionId(brand, index, batch.name)
  }))), [brands]);
  const rows: BatchRow[] = useMemo(() => {
    const direction = sort.direction === 'asc' ? 1 : -1;
    const value = (row: BatchRow) => {
      switch (sort.key) {
        case 'uploaded':
          return uploadedTime(row.arrived);
        case 'batch':
          return row.name.toLocaleLowerCase();
        case 'brand':
          return row.brand.toLocaleLowerCase();
        case 'units':
          return row.received;
        case 'msrp':
          return row.msrp ?? 0;
        case 'recovery':
          return row.estimatedRecovery ?? 0;
      }
    };
    return [...unsortedRows].sort((a, b) => {
      const left = value(a);
      const right = value(b);
      if (typeof left === 'string' || typeof right === 'string') return String(left).localeCompare(String(right)) * direction;
      return (left - right) * direction;
    });
  }, [unsortedRows, sort]);
  const pageCount = Math.max(1, Math.ceil(rows.length / ROWS_PER_PAGE));
  const currentPage = Math.min(page, pageCount);
  const pageRows = rows.slice((currentPage - 1) * ROWS_PER_PAGE, currentPage * ROWS_PER_PAGE);
  const isSelected = (row: BatchRow) => !deselected.includes(row.id);
  const toggleBatch = (row: BatchRow) => setDeselected(current => current.includes(row.id) ? current.filter(id => id !== row.id) : [...current, row.id]);
  const allSelected = deselected.length === 0;
  const toggleAll = () => setDeselected(allSelected ? rows.map(row => row.id) : []);

  // Each brand contributes only its selected submissions. Brands whose batch
  // records carry no valuation fall back to a share of their known totals.
  const totals = useMemo(() => {
    return brands.reduce((sum, {
      brand,
      batches,
      base
    }) => {
      const selected = batches.filter((batch, index) => !deselected.includes(`${brand}::${index}::${batch.name}`));
      const brandUnits = batches.reduce((count, batch) => count + batch.received, 0);
      const selectedUnits = selected.reduce((count, batch) => count + batch.received, 0);
      const share = batches.length === 0 ? 1 : brandUnits > 0 ? selectedUnits / brandUnits : selected.length / batches.length;
      const scale = (value: number) => Math.round(value * share);
      const lines = selected.reduce((count, batch) => count + (batch.lineItems ?? 0), 0);
      const batchMsrp = selected.reduce((value, batch) => value + (batch.msrp ?? 0), 0);
      const batchRecovery = selected.reduce((value, batch) => value + (batch.estimatedRecovery ?? 0), 0);
      const matched = brand === 'Nisolo' ? selected.reduce((result, submission) => {
        const outcome = reconcileSubmission(submission, nisoloOrders);
        return {
          units: result.units + outcome.soldUnits,
          sales: result.sales + outcome.actualSales
        };
      }, {
        units: 0,
        sales: 0
      }) : {
        units: scale(base.soldUnits),
        sales: scale(base.totalSales)
      };
      return {
        products: sum.products + (lines > 0 ? lines : scale(base.receivedProducts)),
        lines: sum.lines + lines,
        units: sum.units + (batches.length > 0 ? selectedUnits : base.receivedUnits),
        msrp: sum.msrp + (batchMsrp > 0 ? batchMsrp : scale(base.totalMsrp)),
        recovery: sum.recovery + (batchRecovery > 0 ? batchRecovery : scale(base.maxOpportunity)),
        soldUnits: sum.soldUnits + matched.units,
        totalSales: sum.totalSales + matched.sales
      };
    }, {
      products: 0,
      lines: 0,
      units: 0,
      msrp: 0,
      recovery: 0,
      soldUnits: 0,
      totalSales: 0
    });
  }, [brands, deselected, nisoloOrders]);
  const receivedProducts = totals.products;
  const receivedUnits = totals.units;
  const selectedLines = totals.lines;
  const predictedRecovery = totals.recovery;
  const perUnit = (value: number, units: number) => units > 0 ? (value / units).toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 2
  }) : '—';
  const summaryMetrics: SummaryMetric[] = [{
    label: 'Unit count',
    value: receivedUnits.toLocaleString('en-US'),
    detail: `Across ${receivedProducts.toLocaleString('en-US')} ${selectedLines > 0 ? 'variant lines' : 'products'}`
  }, {
    label: 'Total MSRP value',
    value: currency(totals.msrp),
    detail: 'Submitted retail value'
  }, {
    label: 'Predicted recovery',
    value: currency(predictedRecovery),
    detail: 'Carli estimate'
  }, {
    label: 'Predicted recovery / unit',
    value: perUnit(predictedRecovery, receivedUnits),
    detail: 'Per unit received'
  }];
  return <div className="mx-auto max-w-7xl px-5 py-10 sm:px-8 lg:py-14" aria-labelledby="inventory-heading" data-id="element-1384" data-mp="zh75d84t03j">
      <section className="flex flex-col gap-5 border-b border-ink/10 pb-8 sm:flex-row sm:items-end sm:justify-between" data-id="element-1385" data-mp="3t3jgm4btyll5">
        <div data-id="element-1386" data-mp="apk1bgqzok3g">
          <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-[#BB5E40]" data-id="element-1387" data-mp="aq3kf0sdlej2">
            <ArchiveIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-1388" data-mp="km6l027kx53w" />
            Inventory
          </span>
          <h1 id="inventory-heading" className="mt-3 font-display text-4xl font-medium tracking-tight sm:text-5xl" data-id="element-1389" data-mp="fnvcbut0t0n9">
            Inventory dashboard
          </h1>
          <p className="mt-3 max-w-2xl text-base leading-relaxed text-ink/65 sm:text-lg" data-id="element-1390" data-mp="tl0li3o16fmq">
            A clear view of inventory received, prepared for sale, and currently live across your approved recovery channels.
          </p>
        </div>
        <span className="inline-flex w-fit items-center gap-2 rounded-full bg-forest/8 px-3 py-1.5 text-xs font-semibold text-forest" data-id="element-1391" data-mp="2qg6kgzxlrc0u">
          <span className="h-1.5 w-1.5 rounded-full bg-forest" data-id="element-1392" data-mp="ue35nt75pqan" />
          {selectedOwner || 'All brands'}
        </span>
      </section>

      <div className="mt-6 flex gap-1 rounded-full border border-ink/10 bg-cream/60 p-1 w-fit" role="tablist" aria-label="Inventory sections" data-id="element-1393" data-mp="14ag0wvicoc7i">
        {(['Submissions', 'Units'] as const).map(tab => <button key={tab} type="button" role="tab" aria-selected={section === tab} onClick={() => setSection(tab)} className={`rounded-full px-4 py-2 text-sm font-semibold transition-colors ${section === tab ? 'bg-[#BB5E40] text-cream shadow-sm' : 'text-ink/55 hover:text-ink'}`} data-id="element-1394" data-mp="1dbe2761em5me">
            {tab}
          </button>)}
      </div>

      {section === 'Units' ? <div className="mt-6" data-id="element-1395" data-mp="1w3f9mws8v47f">
          <UnitsDashboard selectedOwner={selectedOwner} embedded data-id="element-1396" data-mp="210oyobbq1duq" />
        </div> : <>
      <section className="mt-6" aria-label="Inventory overview" data-id="element-1397" data-mp="3lipwitn49kx2">
        <article className="rounded-3xl border border-ink/10 bg-paper p-5 shadow-sm sm:p-7" aria-labelledby="summary-heading" data-id="element-1398" data-mp="3lk1474jonctg">
          <div className="flex items-center gap-2" data-id="element-1399" data-mp="3ci1mea0ug4pg">
            <BoxesIcon className="h-4 w-4 text-[#BB5E40]" aria-hidden="true" data-id="element-1400" data-mp="3ciumwt97lxz5" />
            <h2 id="summary-heading" className="text-lg font-semibold" data-id="element-1401" data-mp="37jq7vlzlt60w">Summary</h2>
          </div>
          <div className="mt-6 grid gap-x-5 gap-y-6 sm:grid-cols-2 lg:grid-cols-4" data-id="element-1402" data-mp="84376zog16m6">
            {summaryMetrics.map((metric, index) => <div key={metric.label} className={`border-[#BB5E40]/25 ${index === 0 ? 'pl-0' : 'sm:border-l-2 sm:pl-4'} ${index % 2 === 0 ? 'sm:border-l-0 sm:pl-0 lg:border-l-2 lg:pl-4' : ''} ${index === 0 ? 'lg:border-l-0 lg:pl-0' : ''}`} data-id="element-1403" data-mp="431bgstqjcoy" data-mpi={index}>
                <p className="text-sm text-ink/55" data-id="element-1404" data-mp="rsc8bz7ysmxt">{metric.label}</p>
                <p className="mt-1 font-display text-3xl font-semibold tracking-tight text-ink" data-id="element-1405" data-mp="rtnbpxxhzsoe">{metric.value}</p>
                <p className="mt-1 text-xs text-ink/50" data-id="element-1406" data-mp="6ll0tnbze1xb">{metric.detail}</p>
              </div>)}
          </div>
        </article>
      </section>

      <section className="mt-5" data-id="element-1407" data-mp="bm8zmv6gbn23">
        <article className="overflow-hidden rounded-2xl border border-ink/10 bg-paper" aria-labelledby="incoming-heading" data-id="element-1408" data-mp="bibi2dziv3kb">
          <div className="flex flex-wrap items-center gap-x-4 gap-y-3 border-b border-ink/10 px-5 py-5 sm:px-6" data-id="element-1409" data-mp="2jhnsu9nyp9x">
            <div className="flex items-center gap-2" data-id="element-1410" data-mp="q9c0nbtzgdu6">
              <ClipboardListIcon className="h-4 w-4 text-[#BB5E40]" aria-hidden="true" data-id="element-1411" data-mp="qby5v4teih98" />
              <h2 id="incoming-heading" className="text-base font-semibold" data-id="element-1412" data-mp="ud9vvbg6t4hy">Inventory submissions</h2>
            </div>
            {pageCount > 1 && <nav className="flex items-center gap-1.5 text-[13px]" aria-label="Submission pages" data-id="element-1413" data-mp="li2dqiztjolz">
                <button type="button" onClick={() => setPage(Math.max(1, currentPage - 1))} disabled={currentPage === 1} className="rounded-lg border border-ink/15 p-1 text-ink/60 transition-colors hover:border-ink/30 hover:text-ink disabled:opacity-35 disabled:hover:border-ink/15 disabled:hover:text-ink/60" aria-label="Previous page" data-id="element-1414" data-mp="197etk1pniux9">
                  <ChevronLeftIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-1415" data-mp="1x5w154vo0yrx" />
                </button>
                <span className="tabular-nums text-ink/60" data-id="element-1416" data-mp="1s715h2clnfqe">
                  Page <span className="font-semibold text-ink" data-id="element-1417" data-mp="14fmaxvr2e2pp">{currentPage}</span> of {pageCount}
                </span>
                <button type="button" onClick={() => setPage(Math.min(pageCount, currentPage + 1))} disabled={currentPage === pageCount} className="rounded-lg border border-ink/15 p-1 text-ink/60 transition-colors hover:border-ink/30 hover:text-ink disabled:opacity-35 disabled:hover:border-ink/15 disabled:hover:text-ink/60" aria-label="Next page" data-id="element-1418" data-mp="10fbshjm35jty">
                  <ChevronRightIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-1419" data-mp="2sutipoitrq55" />
                </button>
              </nav>}
            <p className="ml-auto text-[13px] text-ink/50" data-id="element-1420" data-mp="2wtu0d3pkgmqk">{rows.length.toLocaleString('en-US')} submissions</p>
          </div>
          <div className="max-h-[420px] overflow-auto" data-id="element-1421" data-mp="36tt7cw6c851b">
            <table className="w-full min-w-[880px] text-left text-sm" data-id="element-1422" data-mp="36tb2xc9iukib">
              <thead className="sticky top-0 z-10 bg-cream text-xs text-ink/50" data-id="element-1423" data-mp="296u7ydo9gizq">
                <tr data-id="element-1424" data-mp="2e4vzzo3nzsnc">
                  <th scope="col" className="py-3 pl-5 pr-2 font-semibold sm:pl-6" data-id="element-1425" data-mp="2e4due0v4422r">
                    <span className="flex items-center gap-2.5" data-id="element-1426" data-mp="2j3qhmmpwbb5v">
                      <SelectBox checked={allSelected} onChange={toggleAll} label="Select all submissions" data-id="element-1427" data-mp="2j2fg6tjxafhx" />
                      <SortButton label="Uploaded" sortKey="uploaded" sort={sort} onSort={toggleSort} data-id="element-1428" data-mp="1qfky23akvs7m" />
                    </span>
                  </th>
                  <th scope="col" className="px-3 py-3 font-semibold" data-id="element-1429" data-mp="1vdczbnx44qoh"><SortButton label="Batch" sortKey="batch" sort={sort} onSort={toggleSort} data-id="element-1430" data-mp="1nv4ztvtvz8fk" /></th>
                  {allBrands && <th scope="col" className="px-3 py-3 font-semibold" data-id="element-1431" data-mp="1ivippv9cu8az"><SortButton label="Brand" sortKey="brand" sort={sort} onSort={toggleSort} data-id="element-1432" data-mp="1iwtrjpj8p5zg" /></th>}
                  <th scope="col" className="px-3 py-3 text-right font-semibold" data-id="element-1433" data-mp="1dv4gwazxiubw"><SortButton label="Submitted" sortKey="units" sort={sort} onSort={toggleSort} align="right" data-id="element-1434" data-mp="18ynqtb9665w6" /></th>
                  <th scope="col" className="px-3 py-3 text-right font-semibold" data-id="element-1435" data-mp="14x2im37kyfsb">Accepted</th>
                  <th scope="col" className="px-3 py-3 text-right font-semibold" data-id="element-1436" data-mp="104slo381sfgj">Acceptance rate</th>
                  <th scope="col" className="px-3 py-3 text-right font-semibold" data-id="element-1437" data-mp="v797t4f9i4p4"><SortButton label="MSRP value" sortKey="msrp" sort={sort} onSort={toggleSort} align="right" data-id="element-1438" data-mp="q7mwj0izuyiy" /></th>
                  <th scope="col" className="px-3 py-3 text-right font-semibold" data-id="element-1439" data-mp="l8jod77pwc6r"><SortButton label="Carli forecast" sortKey="recovery" sort={sort} onSort={toggleSort} align="right" data-id="element-1440" data-mp="la4iwwu17v4m" /></th>
                  <th scope="col" className="px-5 py-3 text-right font-semibold sm:px-6" data-id="element-1441" data-mp="252uxd3sy6y7y">Guardrails</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-ink/10" data-id="element-1442" data-mp="2a1z7pxrlz1zv">
                {pageRows.length > 0 ? pageRows.map(row => {
                  const checked = isSelected(row);
                  const rules = rulesFor(row.id, row.brand, row.name);
                  const custom = !rulesMatchDefaults(rules, row.brand, row.name);
                  return <React.Fragment key={row.id} data-id="element-1443" data-mp="1r4509o64oyjl">
                    <tr className={checked ? '' : 'opacity-45'} data-id="element-1444" data-mp="1r4xwvwbdi7o0">
                      <td className="whitespace-nowrap py-4 pl-5 pr-2 text-ink/60 sm:pl-6" data-id="element-1445" data-mp="1cfibywyiwwg8">
                        <span className="flex items-center gap-2.5" data-id="element-1446" data-mp="1hd0m2qoa1hpt">
                          <SelectBox checked={checked} onChange={() => toggleBatch(row)} label={`Include ${row.name} in totals`} data-id="element-1447" data-mp="13e0upb9dqopy" />
                          {row.arrived}
                        </span>
                      </td>
                      <th scope="row" className="px-3 py-4 text-left font-medium text-ink" data-id="element-1448" data-mp="oqk2q7gtutxi">{row.name}</th>
                      {allBrands && <td className="whitespace-nowrap px-3 py-4 text-ink/60" data-id="element-1449" data-mp="jtk6spdoz7k4">{row.brand}</td>}
                      <td className="px-3 py-4 text-right font-semibold tabular-nums text-ink" data-id="element-1450" data-mp="xt35ysoiz8ka">{row.received.toLocaleString('en-US')}</td>
                      <td className="px-3 py-4 text-right font-semibold tabular-nums text-ink" data-id="element-1451" data-mp="sv1httc8sj1y">{row.received.toLocaleString('en-US')}</td>
                      <td className="px-3 py-4 text-right tabular-nums text-ink/70" data-id="element-1452" data-mp="12rmrwt34ckcn">100%</td>
                      <td className="px-3 py-4 text-right tabular-nums text-ink/70" data-id="element-1453" data-mp="1gj19qwlaifi4">{row.msrp && row.msrp > 0 ? currency(row.msrp) : '—'}</td>
                      <td className="px-3 py-4 text-right" data-id="element-1454" data-mp="1chg0dl7ysjco">
                        <ForecastCell row={row} onOpen={() => setReportRow(row)} data-id="element-1455" data-mp="1qg5zt7i9av47" />
                      </td>
                      <td className="whitespace-nowrap px-5 py-4 text-right sm:px-6" data-id="element-1456" data-mp="1lidy5lrt7umt">
                        <button type="button" onClick={() => setGuardrailRow(row)} className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${custom ? 'border-[#BB5E40] bg-[#BB5E40]/8 text-[#94452E]' : 'border-ink/15 text-ink/60 hover:border-[#BB5E40]/40 hover:text-ink'}`} data-id="element-1457" data-mp="1xzt52kwr1bs1">
                          <SlidersHorizontalIcon className="h-3 w-3" aria-hidden="true" data-id="element-1458" data-mp="1ab9yc9tcl722" />
                          {custom ? summarizeRules(rules) : 'See settings'}
                        </button>
                      </td>
                    </tr>
                    </React.Fragment>;
                }) : <tr data-id="element-1459" data-mp="wibojig5rcez">
                    <td colSpan={allBrands ? 9 : 8} className="px-5 py-10 text-center text-sm text-ink/55 sm:px-6" data-id="element-1460" data-mp="whiof0bpfl5t">No inventory submissions yet.</td>
                  </tr>}
              </tbody>
            </table>
          </div>
        </article>
      </section>

      {nisoloActive && <section className="mt-5" aria-label="AI insights" data-id="element-1461" data-mp="37zn8l9tjifq7">
          <UploadAIInsights uploads={(nisoloSubmissions?.submissions ?? []).filter((submission, index) => !deselected.includes(`Nisolo::${index}::${submission.name}`))} orders={nisoloOrders} loading={submissionsLoading} error={submissionsError} data-id="element-1462" data-mp="2ta7lq4x4qufs" />
        </section>}
        </>}

      {reportRow && <div className="fixed inset-0 z-50 flex items-end justify-center bg-ink/40 p-4 sm:items-center" role="dialog" aria-modal="true" aria-label={`Carli report for ${reportRow.name}`} onClick={() => setReportRow(null)} data-id="element-1463" data-mp="3cxp08j517nda">
          <div className="max-h-[90vh] w-full max-w-5xl overflow-y-auto rounded-3xl border border-ink/10 bg-paper p-6 shadow-xl sm:p-8" onClick={event => event.stopPropagation()} data-id="element-1464" data-mp="21kmihkr74rrj">
            <div className="flex items-start justify-between gap-4" data-id="element-1465" data-mp="72bhybre6voa">
              <div data-id="element-1466" data-mp="744lhi6pkzs2">
                <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-[#94452E]" data-id="element-1467" data-mp="2b1sbb5ufsce">
                  <CarliStars data-id="element-1468" data-mp="3tgl2g9s7bjjs" />
                  Carli forecast
                </span>
                <h2 className="mt-2 text-lg font-semibold" data-id="element-1469" data-mp="3pgkfu1uy821n">{reportRow.name}</h2>
                <p className="mt-1 text-sm text-ink/55" data-id="element-1470" data-mp="3kgy2ztj18ntd">
                  {reportRow.brand} · {reportRow.received.toLocaleString('en-US')} units · {reportRow.estimatedRecovery ? currency(reportRow.estimatedRecovery) : '—'} forecast
                </p>
              </div>
              <button type="button" onClick={() => setReportRow(null)} aria-label="Close report" className="rounded-full p-2 text-ink/50 transition-colors hover:bg-cream hover:text-ink" data-id="element-1471" data-mp="3akuzokcg2j4u">
                <XIcon className="h-4 w-4" data-id="element-1472" data-mp="1h7mkhbsi907l" />
              </button>
            </div>

            <div className="mt-5 space-y-5" data-id="element-1473" data-mp="1l97rwhm9smae">
              <ConfidenceDetail row={reportRow} data-id="element-1474" data-mp="1l7wpom8h3mle" />
              {reportRow.brand === 'Nisolo' && <UploadAIInsights uploads={(nisoloSubmissions?.submissions ?? []).filter(submission => submission.name === reportRow.name)} orders={nisoloOrders} loading={submissionsLoading} error={submissionsError} defaultOpen data-id="element-1475" data-mp="1q2st0ij5twdt" />}
            </div>
          </div>
        </div>}

      {guardrailRow && <div className="fixed inset-0 z-50 flex items-end justify-center bg-ink/40 p-4 sm:items-center" role="dialog" aria-modal="true" aria-label={`Guardrails for ${guardrailRow.name}`} onClick={() => setGuardrailRow(null)} data-id="element-1476" data-mp="1c91gd1a4twdv">
          <div className="max-h-[88vh] w-full max-w-5xl overflow-y-auto rounded-3xl border border-ink/10 bg-paper p-6 shadow-xl sm:p-8" onClick={event => event.stopPropagation()} data-id="element-1477" data-mp="g0xyvruftbh">
            <div className="flex items-start justify-between gap-4" data-id="element-1478" data-mp="9h8i89xiytmk">
              <div data-id="element-1479" data-mp="9ecl2nti48z6">
                <p className="text-xs font-semibold uppercase tracking-wider text-[#94452E]" data-id="element-1480" data-mp="4hvyhy1ya2o7">Guardrails</p>
                <h2 className="mt-1 text-lg font-semibold" data-id="element-1481" data-mp="jed3m7jjql2b">{guardrailRow.name}</h2>
                <p className="mt-1 text-sm text-ink/55" data-id="element-1482" data-mp="eg1w43gq4mn9">
                  {guardrailRow.brand} · {guardrailRow.received.toLocaleString('en-US')} units uploaded {guardrailRow.arrived}
                </p>
                <p className="mt-2 text-xs font-medium text-ink/45" data-id="element-1483" data-mp="o5v8h6oox5sk">View only</p>
                {onOpenSettings && <button type="button" onClick={() => {
              const id = guardrailRow.id;
              setGuardrailRow(null);
              onOpenSettings(id);
            }} className="mt-3 inline-flex items-center gap-1.5 rounded-full border border-[#BB5E40] px-3 py-1.5 text-xs font-semibold text-[#94452E] transition-colors hover:bg-[#BB5E40]/8" data-id="element-1484" data-mp="124l6qpn8xbii">
                    Go to Settings to change guardrails
                    <ArrowUpRightIcon className="h-3 w-3" aria-hidden="true" data-id="element-1485" data-mp="1brf9w8eye6wg" />
                  </button>}
              </div>
              <button type="button" onClick={() => setGuardrailRow(null)} aria-label="Close guardrails" className="rounded-full p-2 text-ink/50 transition-colors hover:bg-cream hover:text-ink" data-id="element-1486" data-mp="12sbsd8vfpzk2">
                <XIcon className="h-4 w-4" data-id="element-1487" data-mp="t0eefud1kthn" />
              </button>
            </div>

            <div className="mt-5" data-id="element-1488" data-mp="f39bi38mkaqm">
              <SubmissionRules batchName={guardrailRow.name} brand={guardrailRow.brand} canUnlock={isAdmin} readOnly rules={rulesFor(guardrailRow.id, guardrailRow.brand, guardrailRow.name)} onChange={next => setRulesFor(guardrailRow.id, next)} onReset={() => resetRulesFor(guardrailRow.id)} data-id="element-1489" data-mp="j4bcopm3qwv8" />
            </div>
          </div>
        </div>}
    </div>;
}

// Forecast value plus Carli's confidence in it. Opens the upload's full report.
function ForecastCell({
  row,
  onOpen
}: {
  row: BatchRow;
  onOpen: () => void;
}) {
  const value = row.estimatedRecovery && row.estimatedRecovery > 0 ? currency(row.estimatedRecovery) : null;
  const confidence = scoreConfidence(row);
  if (!value) return <span className="text-sm text-ink/40" data-id="element-1490" data-mp="2qqmaeawbxyax">—</span>;
  const tone = !confidence ? 'border-ink/15 bg-cream text-ink/60' : confidence.label === 'High' ? 'border-forest/30 bg-forest/8 text-forest' : confidence.label === 'Medium' ? 'border-[#BB5E40]/30 bg-[#BB5E40]/8 text-[#94452E]' : 'border-ink/15 bg-cream text-ink/60';
  return <button type="button" onClick={onOpen} className="group inline-flex flex-col items-end gap-1 rounded-lg px-1.5 py-1 transition-colors hover:bg-cream/70" aria-label={`Open Carli report for ${row.name}`} data-id="element-1491" data-mp="3qw6lji46i3yd">
      <span className="flex items-center gap-1.5 font-semibold tabular-nums text-ink" data-id="element-1492" data-mp="8ktllgcgzm9g">
        {value}
        <ChevronRightIcon className="h-3.5 w-3.5 text-ink/35 transition-colors group-hover:text-[#BB5E40]" aria-hidden="true" data-id="element-1493" data-mp="dk663ufvch8v" />
      </span>
      {confidence && <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-semibold tabular-nums ${tone}`} data-id="element-1494" data-mp="ikapbs85vq2s">
          {confidence.label} · {confidence.score}%
        </span>}
    </button>;
}

// The breakdown behind an upload's confidence score, shown under its row.
function ConfidenceDetail({
  row
}: {
  row: BatchRow;
}) {
  const confidence = scoreConfidence(row);
  if (!confidence) return null;
  return <div className="rounded-2xl border border-[#BB5E40]/20 bg-cream/45 p-4 sm:p-5" data-id="element-1495" data-mp="z1vl1bai40eh">
      <div className="flex flex-wrap items-center gap-2" data-id="element-1496" data-mp="v1lg81yg3g17">
        <CarliStars data-id="element-1497" data-mp="uyzb08z11cm5" />
        <p className="text-xs font-semibold uppercase tracking-wider text-[#94452E]" data-id="element-1498" data-mp="18sgr1jsi877f">{confidence.label} confidence · {confidence.score}%</p>
        <span className="text-xs text-ink/55" data-id="element-1499" data-mp="13uezsbkxd1kv">How much of {row.name} Carli could model when estimating recovery.</span>
      </div>
      <ul className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-5" data-id="element-1500" data-mp="g42r2yhuv0t8">
        {confidence.factors.map(factor => <li key={factor.label} data-id="element-1501" data-mp="c4ka2dt5n9vo">
            <div className="flex items-baseline justify-between gap-2" data-id="element-1502" data-mp="q28ujkjb8f07">
              <span className="text-xs font-semibold text-ink" data-id="element-1503" data-mp="l2vxy82mztkf">{factor.label}</span>
              <span className="text-xs font-semibold tabular-nums text-ink/55" data-id="element-1504" data-mp="l58p9buabga5">{Math.round(factor.score * 100)}%</span>
            </div>
            <div className="mt-1.5 h-1 w-full rounded-full bg-ink/10" data-id="element-1505" data-mp="1wiknn779z6ox">
              <div className="h-1 rounded-full bg-[#BB5E40]" style={{
            width: `${Math.round(factor.score * 100)}%`
          }} data-id="element-1506" data-mp="3efnwtpol8w74" />
            </div>
            <p className="mt-1.5 text-[11px] leading-relaxed text-ink/55" data-id="element-1507" data-mp="3jf0t111cnfmd">{factor.detail}</p>
          </li>)}
      </ul>
    </div>;
}
function SortButton({
  label,
  sortKey,
  sort,
  onSort,
  align = 'left'
}: {
  label: string;
  sortKey: SortKey;
  sort: {
    key: SortKey;
    direction: 'asc' | 'desc';
  };
  onSort: (key: SortKey) => void;
  align?: 'left' | 'right';
}) {
  const active = sort.key === sortKey;
  return <button type="button" onClick={() => onSort(sortKey)} aria-label={`Sort by ${label}`} className={`inline-flex items-center gap-1 font-semibold transition-colors hover:text-ink ${active ? 'text-ink' : 'text-ink/50'} ${align === 'right' ? 'flex-row-reverse' : ''}`} data-id="element-1508" data-mp="5zcpwuua00q2">
      {label}
      <ChevronUpIcon className={`h-3 w-3 shrink-0 transition-transform ${active ? 'opacity-100' : 'opacity-0'} ${active && sort.direction === 'desc' ? 'rotate-180' : ''}`} aria-hidden="true" data-id="element-1509" data-mp="kv22619nszhl" />
    </button>;
}
function SelectBox({
  checked,
  onChange,
  label
}: {
  checked: boolean;
  onChange: () => void;
  label: string;
}) {
  return <button type="button" role="checkbox" aria-checked={checked} aria-label={label} onClick={onChange} className={`flex h-4 w-4 shrink-0 items-center justify-center rounded border transition-colors ${checked ? 'border-[#BB5E40] bg-[#BB5E40] text-cream' : 'border-ink/25 bg-paper hover:border-[#BB5E40]/50'}`} data-id="element-1510" data-mp="21dfeo3xcohuk">
      {checked && <CheckIcon className="h-3 w-3" strokeWidth={3} aria-hidden="true" data-id="element-1511" data-mp="1hpof8gj9g7y8" />}
    </button>;
}